if (!customElements.get('product-card')) {
    class ProductCard extends HTMLElement {
        connectedCallback() {
            this.handleClick = this.handleClick.bind(this);
            this.handleSwymEvent = this.handleSwymEvent.bind(this);
            this.addEventListener('click', this.handleClick);
            this.sizeSelector = this.querySelector('[data-size-selector]');
            this.variantMap = this.getVariantMap();
            const initialProductId = this.dataset.selectedProductId || this.getFirstProductId();
            if (initialProductId) {
                this.updateSizes(initialProductId, this.dataset.selectedSize);
                this.setSelectedSwatch(initialProductId);
                this.updateBrand(initialProductId);
                this.updateFinalSale(initialProductId);
                this.updateFlags(initialProductId);

                const product = this.variantMap?.[initialProductId];
                if (product?.handle) {
                    const variants = product.variants || [];
                    const firstAvailableVariant = variants.find((v) => v.available) || variants[0];
                    if (firstAvailableVariant?.id) {
                        this.updateProductLinks(initialProductId, String(firstAvailableVariant.id));
                        this.updatePrice(initialProductId, String(firstAvailableVariant.id));
                        this.updateWishlistButton(initialProductId, String(firstAvailableVariant.id));
                    }
                }
            }

            window.addEventListener('sw:addedtowishlist', this.handleSwymEvent);
            window.addEventListener('sw:removedfromwishlist', this.handleSwymEvent);

            if (window._swat) {
                this.initSwym(window._swat);
            } else {
                if (!window.SwymCallbacks) window.SwymCallbacks = [];
                window.SwymCallbacks.push((swat) => {
                    this.initSwym(swat);
                });
            }
        }

        initSwym(swat) {
            this.setupSwymEvtLayerListeners();
            
            if (!window.swymWishLists) {
                this.fetchWishlistData().then(() => {
                    this.checkWishlistState();
                });
            } else {
                this.checkWishlistState();
            }
        }

        fetchWishlistData() {
            return new Promise((resolve) => {
                const swat = window._swat;
                if (!swat?.fetchLists) {
                    resolve();
                    return;
                }

                swat.fetchLists({
                    callbackFn: (lists) => {
                        window.swymWishLists = lists;
                        if (lists && lists[0] && lists[0].lid) {
                            window.swymSelectedListId = lists[0].lid;
                        }
                        resolve();
                    },
                    errorFn: (error) => {
                        console.error('Error fetching wishlist data:', error);
                        resolve();
                    },
                });
            });
        }

        disconnectedCallback() {
            this.removeEventListener('click', this.handleClick);
            window.removeEventListener('sw:addedtowishlist', this.handleSwymEvent);
            window.removeEventListener('sw:removedfromwishlist', this.handleSwymEvent);
        }

        setupSwymEvtLayerListeners() {
            const swat = window._swat;
            if (!swat?.evtLayer) return;

            if (swat.JSEvents?.addedToWishlist) {
                swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, () => {
                    this.refreshWishlistData();
                });
            }
            if (swat.JSEvents?.removedFromWishlist) {
                swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, () => {
                    this.refreshWishlistData();
                });
            }
        }

        handleSwymEvent() {
            setTimeout(() => {
                this.refreshWishlistData();
            }, 300);
        }

        handleClick(event) {
            const wishlistBtn = event.target.closest('[data-product-card-wishlist]');
            if (wishlistBtn && this.contains(wishlistBtn)) {
                this.handleWishlistClick(event);
                return;
            }

            const sizeOption = event.target.closest('[data-size-option]');
            if (sizeOption && this.contains(sizeOption)) {
                if (sizeOption.disabled) {
                    return;
                }
                this.setSelectedSize(sizeOption.dataset.sizeOption);
                this.setSelectedVariantId(sizeOption.dataset.variantId);
                return;
            }

            const swatch = event.target.closest('.colour-swatch-hex');
            if (!swatch || !this.contains(swatch)) return;

            const title = swatch.dataset.title;
            const id = swatch.dataset.id;

            const titleEl = this.querySelector('[data-product-card-title]');
            const idEl = this.querySelector('[data-product-card-id]');
            const brandEl = this.querySelector('[data-product-card-brand]');

            if (titleEl && title) titleEl.textContent = title;
            if (idEl && id) idEl.textContent = id;

            if (brandEl && id) {
                const product = this.variantMap?.[id];
                if (product?.vendor) {
                    brandEl.textContent = product.vendor;
                    const vendorUrl = `/collections/vendors/${encodeURIComponent(product.vendor)}`;
                    brandEl.setAttribute('href', vendorUrl);
                }
            }

            this.dataset.selectedProductId = id || '';
            this.setSelectedSwatch(id);
            this.updateFinalSale(id);
            this.updateFlags(id);

            const previousSelectedSize = this.dataset.selectedSize;
            this.updateSizes(id, previousSelectedSize);

            const product = this.variantMap?.[id];
            if (product?.handle && !previousSelectedSize) {
                const variants = product.variants || [];
                const firstAvailableVariant = variants.find((v) => v.available) || variants[0];
                if (firstAvailableVariant?.id) {
                    this.updateProductLinks(id, String(firstAvailableVariant.id));
                    this.updatePrice(id, String(firstAvailableVariant.id));
                }
            }
        }

        getVariantMap() {
            const mapId = this.dataset.variantMapId;
            const mapEl = mapId
                ? this.querySelector(`#${mapId}`)
                : this.querySelector('script[type="application/json"]');
            if (!mapEl) return {};
            try {
                return JSON.parse(mapEl.textContent);
            } catch (error) {
                return {};
            }
        }

        getFirstProductId() {
            const keys = Object.keys(this.variantMap || {});
            return keys.length ? keys[0] : '';
        }

        updateSizes(productId, preferredSize) {
            if (!this.sizeSelector) return;
            const product = this.variantMap?.[productId];
            const variants = product?.variants || [];
            const sizes = [];
            const sizeToVariantId = {};
            const sizeToVariantAvailable = {};

            for (const variant of variants) {
                const sizeLabel = variant?.option1 || variant?.title;
                if (!sizeLabel) continue;
                if (!sizes.includes(sizeLabel)) {
                    sizes.push(sizeLabel);
                }

                const isAvailable = variant?.available === true;

                if (!sizeToVariantId[sizeLabel] || (isAvailable && sizeToVariantAvailable[sizeLabel] === false)) {
                    sizeToVariantId[sizeLabel] = String(variant.id || '');
                    sizeToVariantAvailable[sizeLabel] = isAvailable;
                }
            }

            if (!sizes.length) {
                this.sizeSelector.innerHTML = '';
                this.dataset.selectedSize = '';
                return;
            }

            let selectedSize = sizes.includes(preferredSize) ? preferredSize : sizes[0];
            if (preferredSize && sizeToVariantAvailable[preferredSize] === false) {
                const availableSize = sizes.find((size) => sizeToVariantAvailable[size] === true);
                if (availableSize) {
                    selectedSize = availableSize;
                }
            }

            this.dataset.selectedSize = selectedSize;
            this.renderSizes(sizes, selectedSize, sizeToVariantId, sizeToVariantAvailable);
            this.setSelectedVariantId(sizeToVariantId[selectedSize]);
        }

        renderSizes(sizes, selectedSize, sizeToVariantId, sizeToVariantAvailable) {
            this.sizeSelector.innerHTML = '';
            for (const size of sizes) {
                const button = document.createElement('button');
                button.type = 'button';
                const isAvailable = sizeToVariantAvailable?.[size] !== false;
                button.className = 'size-option';
                if (!isAvailable) {
                    button.classList.add('size-disabled');
                    button.disabled = true;
                }
                button.dataset.sizeOption = size;
                button.dataset.variantId = sizeToVariantId?.[size] || '';
                button.textContent = size;
                button.setAttribute('aria-pressed', size === selectedSize ? 'true' : 'false');
                button.setAttribute('aria-disabled', !isAvailable ? 'true' : 'false');
                if (size === selectedSize && isAvailable) {
                    button.classList.add('is-selected');
                }
                this.sizeSelector.appendChild(button);
            }
        }

        setSelectedSize(size) {
            if (!this.sizeSelector || !size) return;
            const buttons = this.sizeSelector.querySelectorAll('[data-size-option]');
            const targetButton = Array.from(buttons).find((btn) => btn.dataset.sizeOption === size);

            if (targetButton && targetButton.disabled) {
                return;
            }

            this.dataset.selectedSize = size;
            buttons.forEach((button) => {
                const isSelected = button.dataset.sizeOption === size;
                if (isSelected && !button.disabled) {
                    button.classList.add('is-selected');
                } else {
                    button.classList.remove('is-selected');
                }
                button.setAttribute('aria-pressed', isSelected && !button.disabled ? 'true' : 'false');
            });
        }

        setSelectedVariantId(variantId) {
            if (!variantId) return;
            this.dataset.selectedVariantId = String(variantId);
            const variantEl = this.querySelector('[data-product-card-variant-id]');
            if (variantEl) {
                variantEl.textContent = String(variantId);
            }
            const productId = this.dataset.selectedProductId;
            if (productId) {
                this.updateImages(productId, String(variantId));
                this.updateProductLinks(productId, String(variantId));
                this.updatePrice(productId, String(variantId));
            } else {
                console.warn('[setSelectedVariantId] No productId set!');
            }
        }

        updateImages(productId, variantId) {
            const product = this.variantMap?.[productId];
            if (!product) return;
            const primaryImageEl = this.querySelector('[data-product-card-image]');
            if (!primaryImageEl) return;

            const variants = product.variants || [];
            let variantImage = '';
            if (variantId) {
                const variant = variants.find((item) => String(item.id) === String(variantId));
                variantImage = variant?.image || '';
            }

            const primarySrc = variantImage || product.product_image || '';
            this.updateImageElement(primaryImageEl, primarySrc);
        }

        updateImageElement(imageEl, src) {
            if (!imageEl) return;
            if (!src) {
                imageEl.removeAttribute('src');
                imageEl.removeAttribute('srcset');
                imageEl.style.display = 'none';
                return;
            }
            imageEl.setAttribute('src', src);
            imageEl.setAttribute('srcset', src);
            imageEl.style.display = '';
        }

        setSelectedSwatch(productId) {
            if (!productId) return;
            const swatches = this.querySelectorAll('.colour-swatch-hex');
            swatches.forEach((swatch) => {
                const isSelected = swatch.dataset.id === String(productId);
                if (isSelected) {
                    swatch.classList.add('colour-swatch-selected');
                } else {
                    swatch.classList.remove('colour-swatch-selected');
                }
            });
        }

        updateBrand(productId) {
            if (!productId) return;
            const brandEl = this.querySelector('[data-product-card-brand]');
            if (!brandEl) return;

            const product = this.variantMap?.[productId];
            if (product?.vendor) {
                brandEl.textContent = product.vendor;
                const vendorUrl = `/collections/vendors/${encodeURIComponent(product.vendor)}`;
                brandEl.setAttribute('href', vendorUrl);
                brandEl.style.display = '';
            } else {
                brandEl.textContent = '';
                brandEl.removeAttribute('href');
                brandEl.style.display = 'none';
            }
        }

        updateProductLinks(productId, variantId) {
            if (!productId || !variantId) return;
            const product = this.variantMap?.[productId];
            if (!product?.handle) return;

            const links = this.querySelectorAll('[data-product-card-link]');
            const productUrl = `/products/${product.handle}?variant=${variantId}`;
            links.forEach((link) => {
                link.setAttribute('href', productUrl);
            });

            const quickViewBtn = this.querySelector('[data-product-card-quick-view]');
            if (quickViewBtn) {
                quickViewBtn.setAttribute('data-handle', product.handle);
                quickViewBtn.setAttribute('data-variant-id', variantId);
                quickViewBtn.setAttribute('href', productUrl);
            }

            this.updateWishlistButton(productId, variantId);
        }

        updateWishlistButton(productId, variantId) {
            const wishlistBtn = this.querySelector('[data-product-card-wishlist]');
            if (!wishlistBtn) return;

            const product = this.variantMap?.[productId];
            if (!product?.handle) return;

            const shopUrl = window.Shopify?.routes?.root ? window.location.origin : '';
            const productUrl = `${shopUrl}/products/${product.handle}`;

            wishlistBtn.classList.remove('swym-added');

            wishlistBtn.setAttribute('data-product-id', String(productId));
            wishlistBtn.setAttribute('data-variant-id', String(variantId));
            wishlistBtn.setAttribute('data-product-url', productUrl);

            this.checkWishlistState();
        }

        checkWishlistState() {
            const wishlistBtn = this.querySelector('[data-product-card-wishlist]');
            if (!wishlistBtn) return;

            const productId = wishlistBtn.getAttribute('data-product-id');
            const variantId = wishlistBtn.getAttribute('data-variant-id');

            if (!productId || !variantId) return;

            if (window.swymWishLists && Array.isArray(window.swymWishLists)) {
                const isWishlisted = this.isVariantInWishlist(productId, variantId, window.swymWishLists);
                this.updateWishlistButtonState(isWishlisted);
            } else {
                this.fetchAndCheckWishlist(productId, variantId);
            }
        }

        isVariantInWishlist(productId, variantId, wishlists) {
            if (!wishlists || !Array.isArray(wishlists)) return false;
            
                return wishlists.some((list) => {
                return list.listcontents?.some((item) => {
                    const match = String(item.empi) === String(productId) && String(item.epi) === String(variantId);
                    return match;
                });
            });
        }

        fetchAndCheckWishlist(productId, variantId) {
            const swat = window._swat;
            if (!swat?.fetchLists) return;

            swat.fetchLists({
                callbackFn: (lists) => {
                    window.swymWishLists = lists;
                    if (lists && lists[0] && lists[0].lid) {
                        window.swymSelectedListId = lists[0].lid;
                    }
                    const isWishlisted = this.isVariantInWishlist(productId, variantId, lists);
                    this.updateWishlistButtonState(isWishlisted);
                },
                errorFn: (error) => {
                    console.error('Error fetching wishlist:', error);
                },
            });
        }

        isVariantWishlisted(productId, variantId) {
            if (window.swymWishLists && Array.isArray(window.swymWishLists)) {
                return this.isVariantInWishlist(productId, variantId, window.swymWishLists);
            }
            return false;
        }

        updateWishlistButtonState(isWishlisted) {
            const wishlistBtn = this.querySelector('[data-product-card-wishlist]');
            if (!wishlistBtn) return;

            if (isWishlisted) {
                wishlistBtn.classList.add('swym-added');
            } else {
                wishlistBtn.classList.remove('swym-added');
            }
        }

        handleWishlistClick(event) {
            event.preventDefault();
            event.stopPropagation();

            const wishlistBtn = this.querySelector('[data-product-card-wishlist]');
            if (!wishlistBtn) return;

            const swat = window._swat;
            if (!swat) {
                console.warn('Swym not loaded');
                return;
            }

            const productId = wishlistBtn.getAttribute('data-product-id');
            const variantId = wishlistBtn.getAttribute('data-variant-id');
            const productUrl = wishlistBtn.getAttribute('data-product-url');

            if (!productId || !variantId) return;

            const swymProduct = {
                epi: variantId,
                empi: productId,
                du: productUrl,
            };

            const isCurrentlyWishlisted = this.isVariantInWishlist(productId, variantId, window.swymWishLists);
            
            const lid = window.swymSelectedListId;

            if (!lid) {
                console.warn('No wishlist ID available, attempting to create one...');
                swat.createList(
                    { lname: 'Wishlist' },
                    (result) => {
                        window.swymSelectedListId = result.lid;
                        this.handleWishlistClick(event);
                    },
                    (error) => {
                        console.error('Failed to create wishlist:', error);
                    }
                );
                return;
            }

            if (isCurrentlyWishlisted) {
                swat.deleteFromList(
                    lid,
                    swymProduct,
                    (deleted) => {
                        swat.ui?.showSuccessNotification?.({ message: 'Removed from Wishlist' });
                        this.updateWishlistButtonState(false);
                        this.refreshWishlistData();
                    },
                    (error) => {
                        console.error('Error removing from wishlist:', error);
                        swat.ui?.showErrorNotification?.({ message: 'Error removing from Wishlist' });
                    }
                );
            } else {
                swat.addToList(
                    lid,
                    swymProduct,
                    (added) => {    
                        swat.ui?.showSuccessNotification?.({ message: 'Added to Wishlist' });
                        this.updateWishlistButtonState(true);
                        this.refreshWishlistData();
                    },
                    (error) => {
                        console.error('Error adding to wishlist:', error);
                        swat.ui?.showErrorNotification?.({ message: 'Error adding to Wishlist' });
                    }
                );
            }
        }

        refreshWishlistData() {
            this.fetchWishlistData().then(() => {
                this.checkWishlistState();
            });
        }

        updatePrice(productId, variantId) {
            if (!productId || !variantId) return;
            const product = this.variantMap?.[productId];
            if (!product) return;

            const variants = product.variants || [];
            const variant = variants.find((v) => String(v.id) === String(variantId));
            const priceEl = this.querySelector('[data-product-card-price-value]');
            const comparePriceEl = this.querySelector('[data-product-card-price-compare-at]');
            const priceWrapper = priceEl ? priceEl.closest('.product-card__price-wrapper') : null;

            if (!variant || !priceEl) {
                if (priceEl) {
                    priceEl.textContent = '';
                    priceEl.classList.remove('is-sale');
                }
                if (comparePriceEl) {
                    comparePriceEl.textContent = '';
                    comparePriceEl.classList.remove('is-sale');
                }
                if (priceWrapper) {
                    priceWrapper.style.display = 'none';
                }
                return;
            }

            const price = variant.price || 0;
            const compareAtPrice = variant.compare_at_price || 0;

            const isSale = compareAtPrice > 0 && price < compareAtPrice;

            const formatPrice = (cents) => {
                return (cents / 100).toFixed(2);
            };

            priceEl.textContent = `$${formatPrice(price)}`;
            if (priceWrapper) {
                priceWrapper.style.display = '';
            }

            if (comparePriceEl) {
                if (isSale) {
                    comparePriceEl.textContent = `$${formatPrice(compareAtPrice)}`;
                    comparePriceEl.classList.add('is-sale');
                    priceEl.classList.add('is-sale');
                } else {
                    comparePriceEl.textContent = '';
                    comparePriceEl.classList.remove('is-sale');
                    priceEl.classList.remove('is-sale');
                }
            }
        }

        updateFinalSale(productId) {
            if (!productId) return;
            const product = this.variantMap?.[productId];
            if (!product) return;

            const finalSaleEl = this.querySelector('[data-product-card-final-sale]');
            const finalSaleNoReturnsEl = this.querySelector('[data-product-card-final-sale-no-returns]');
            const finalSaleWrapper = this.querySelector('[data-product-card-final-sale-wrapper]');

            const finalSale = product.final_sale || '';
            const finalSaleNoReturns = product.final_sale_no_returns || '';

            if (finalSaleEl) {
                if (finalSale) {
                    finalSaleEl.textContent = finalSale;
                    finalSaleEl.style.display = '';
                } else {
                    finalSaleEl.textContent = '';
                    finalSaleEl.style.display = 'none';
                }
            }

            if (finalSaleNoReturnsEl) {
                if (finalSaleNoReturns) {
                    finalSaleNoReturnsEl.textContent = finalSaleNoReturns;
                    finalSaleNoReturnsEl.style.display = '';
                } else {
                    finalSaleNoReturnsEl.textContent = '';
                    finalSaleNoReturnsEl.style.display = 'none';
                }
            }

            if (finalSaleWrapper) {
                const hasAnyLabel = finalSale || finalSaleNoReturns;
                finalSaleWrapper.style.display = hasAnyLabel ? '' : 'none';
                finalSaleWrapper.style.marginTop = hasAnyLabel ? '6px' : '0';
                finalSaleWrapper.style.marginBottom = hasAnyLabel ? '12px' : '0';
            }
        }

        updateFlags(productId) {
            if (!productId) return;
            const product = this.variantMap?.[productId];
            if (!product) return;

            const flagItem = this.querySelector('[data-product-card-flag-item]');
            const flagText = this.querySelector('[data-product-card-flag-text]');
            if (!flagItem || !flagText) return;

            let flagValue = '';
            if (Array.isArray(product.flags) && product.flags.length) {
                flagValue = product.flags[0];
            } else if (typeof product.flags === 'string') {
                flagValue = product.flags;
            }

            flagItem.classList.forEach((className) => {
                if (className.startsWith('product-badge__item--')) {
                    flagItem.classList.remove(className);
                }
            });

            if (flagValue) {
                flagItem.classList.add(`product-badge__item--${flagValue}`);
                flagText.textContent = flagValue;
                flagItem.style.display = '';
            } else {
                flagText.textContent = '';
                flagItem.style.display = 'none';
            }
        }
    }

    customElements.define('product-card', ProductCard);
}


